import { createContext, memo, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSession } from "@/context/session-context";
import { useWebRtcTransfer } from "@/hooks/use-webrtc-transfer";
import type { Session } from "@/types";

type TransferValue=ReturnType<typeof useWebRtcTransfer>;
const TransferContext=createContext<TransferValue|null>(null);

// The connection lives above the routes: it is built in the background while the pairing screen still asks
// for approval, and carries over to the transfer page once the request is accepted. It runs in a sibling of
// the pages (not a wrapper) so starting or ending a session never remounts the page the user is on.
export function TransferProvider({children}:{children:React.ReactNode}){
  const {session}=useSession();const [current,setCurrent]=useState<{room:string;transfer:TransferValue}|null>(null);
  const report=useCallback((room:string,transfer:TransferValue)=>setCurrent({room,transfer}),[]);
  const value=session&&current?.room===session.room?current.transfer:null;
  return <TransferContext.Provider value={value}>{session&&<SessionTransfer key={session.room} session={session} report={report}/>}{children}</TransferContext.Provider>;
}

// Memoised so the provider re-rendering with a new value does not re-render this and report again in a loop.
const SessionTransfer=memo(function SessionTransfer({session,report}:{session:Session;report:(room:string,transfer:TransferValue)=>void}){
  const {setSession}=useSession();const navigate=useNavigate();const {pathname}=useLocation();const path=useRef(pathname);
  useEffect(()=>{path.current=pathname},[pathname]);
  const end=useCallback(()=>{setSession(null);if(path.current==="/transfer")navigate("/",{replace:true})},[navigate,setSession]);
  const t=useWebRtcTransfer(session,end);
  useEffect(()=>{report(session.room,t)});
  // Approval moves both devices from the pairing screen to the transfer page, with the link already up.
  const wasApproved=useRef(t.approved);
  useEffect(()=>{if(t.approved&&!wasApproved.current&&path.current!=="/transfer")navigate("/transfer");wasApproved.current=t.approved},[navigate,t.approved]);
  // A declined request, or a peer that vanished before approving, ends the background session quietly.
  const pendingEnded=t.declined||(!t.approved&&t.peerLeft);
  useEffect(()=>{if(pendingEnded)setSession(null)},[pendingEnded,setSession]);
  return null;
});

export function useTransfer(){return useContext(TransferContext)}
